import { Type } from '@sinclair/typebox';
import { PoolSchemaExtended, SupportedNetworkSchema, TokenModerationStatusSchema, } from '@blade/blockchain-types';
export var AssetTrend;
(function (AssetTrend) {
    AssetTrend["Up"] = "up";
    AssetTrend["Down"] = "down";
    AssetTrend["Neutral"] = "neutral";
})(AssetTrend || (AssetTrend = {}));
export const AssetSchema = Type.Object({
    address: Type.String(),
    pairAddress: Type.Optional(Type.String()),
    trend: Type.Enum(AssetTrend, {
        type: 'string',
        enum: Object.values(AssetTrend),
    }),
    balance: Type.Number(),
    rawBalance: Type.Optional(Type.String()),
    name: Type.String(),
    symbol: Type.String(),
    decimals: Type.Number(),
    priceUsd: Type.Number(),
    priceNative: Type.Number(),
    logo: Type.String(),
    logoStatus: Type.Optional(TokenModerationStatusSchema),
    hidden: Type.Boolean(),
    pairMeta: Type.Optional(PoolSchemaExtended),
}, { title: 'WalletAssetSchema' });
export const TradeBotWalletSchema = Type.Object({
    walletId: Type.String(),
    delugeId: Type.String(),
    address: Type.String(),
    chain: SupportedNetworkSchema,
    assets: Type.Array(AssetSchema),
    name: Type.String(),
    color: Type.String(),
    favorite: Type.Boolean(),
    order: Type.Optional(Type.Number()),
    /**
     * Whose key it is: 'server' means we hold it and can sign with it,
     * 'device' means only the machine that made it can.
     *
     * Said out loud because the reader has to branch on it — a screen that
     * cannot tell them apart offers to export a key it does not have, and
     * sends a trade down a signing path that will refuse it.
     */
    custody: Type.Optional(Type.Union([Type.Literal('server'), Type.Literal('device')])),
}, { title: 'TradeBotWalletSchema' });
export var ServerEvent;
(function (ServerEvent) {
    ServerEvent["Unauthorized"] = "unauthorized";
    ServerEvent["PairUpdate"] = "event:pair-update";
    ServerEvent["PairEvents"] = "event:pair-events";
    ServerEvent["PairPriceUpdate"] = "event:pair-price-update";
    ServerEvent["UserOrder"] = "event:user-order";
    ServerEvent["UserPositionUpdate"] = "event:user-position-update";
    ServerEvent["TrackingWalletAlert"] = "event:tracking-wallet-alert";
    ServerEvent["HolderChartUpdate"] = "event:holder-chart-update";
    ServerEvent["PoolActivity"] = "event:pools-activity";
    ServerEvent["WalletBalanceUpdate"] = "event:wallet-balance-update";
    ServerEvent["ConfirmTransactionUpdate"] = "event:confirm-transaction-update";
    ServerEvent["ReferralCodeUpdate"] = "event:referral-code-update";
    ServerEvent["NFTGenerationProgress"] = "nft-generation-progress";
    ServerEvent["NFTGenerationCompleted"] = "nft-generation-completed";
    ServerEvent["NFTGenerationError"] = "nft-generation-error";
    ServerEvent["UserOnlineStatusUpdate"] = "event:user-online-status-update";
    ServerEvent["SupportTicketUpdated"] = "event:support-ticket-updated";
    ServerEvent["SupportMessageCreated"] = "event:support-message-created";
    ServerEvent["Authenticated"] = "event:authenticated";
})(ServerEvent || (ServerEvent = {}));
export * from './pool-blocks.js';
export * from './pool-blocks-schema.js';
export var ActivityType;
(function (ActivityType) {
    ActivityType["Growth"] = "growth";
    ActivityType["HighestAth"] = "ath";
    ActivityType["PoolMigrated"] = "pool_migrated";
    ActivityType["BoostPurchased"] = "boost_purchased";
    ActivityType["PremiumPurchased"] = "premium_purchased";
})(ActivityType || (ActivityType = {}));
export var ClientEvent;
(function (ClientEvent) {
    ClientEvent["LatencyPing"] = "latency:ping";
    ClientEvent["PairInfoSubscribe"] = "subscribe:pair-info";
    ClientEvent["PairInfoUnsubscribe"] = "unsubscribe:pair-info";
    ClientEvent["PairEventsSubscribe"] = "subscribe:pair-events";
    ClientEvent["PairEventsUnsubscribe"] = "unsubscribe:pair-events";
    ClientEvent["PairPriceSubscribe"] = "subscribe:pair-price";
    ClientEvent["PairPriceUnsubscribe"] = "unsubscribe:pair-price";
    ClientEvent["UserOrderSubscribe"] = "subscribe:new-user-order";
    ClientEvent["UserOrderUnsubscribe"] = "unsubscribe:new-user-order";
    ClientEvent["UserPositionsSubscribe"] = "subscribe:user-positions";
    ClientEvent["UserPositionsUnsubscribe"] = "unsubscribe:user-positions";
    ClientEvent["WalletsTrackerSubscribe"] = "subscribe:wallets-tracker";
    ClientEvent["WalletsTrackerUnsubscribe"] = "unsubscribe:wallets-tracker";
    ClientEvent["HolderChartSubscribe"] = "subscribe:holder-chart";
    ClientEvent["HolderChartUnsubscribe"] = "unsubscribe:holder-chart";
    ClientEvent["PoolsActivitySubscribe"] = "subscribe:pools-activity";
    ClientEvent["PoolsActivityUnsubscribe"] = "unsubscribe:pools-activity";
    ClientEvent["WalletBalanceSubscribe"] = "subscribe:wallet-balance";
    ClientEvent["WalletBalanceUnsubscribe"] = "unsubscribe:wallet-balance";
    ClientEvent["ConfirmTransactionSubscribe"] = "subscribe:confirm-transaction";
    ClientEvent["ConfirmTransactionUnsubscribe"] = "unsubscribe:confirm-transaction";
    ClientEvent["ReferralCodeSubscribe"] = "subscribe:referral-code";
    ClientEvent["ReferralCodeUnsubscribe"] = "unsubscribe:referral-code";
    ClientEvent["NFTGenerationSubscribe"] = "subscribe:nft-generation";
    ClientEvent["NFTGenerationUnsubscribe"] = "unsubscribe:nft-generation";
    ClientEvent["UserOnlineStatusSubscribe"] = "subscribe:user-online-status";
    ClientEvent["UserOnlineStatusUnsubscribe"] = "unsubscribe:user-online-status";
    ClientEvent["SupportUserTicketsSubscribe"] = "subscribe:support-user-tickets";
    ClientEvent["SupportUserTicketsUnsubscribe"] = "unsubscribe:support-user-tickets";
    ClientEvent["SupportTicketSubscribe"] = "subscribe:support-ticket";
    ClientEvent["SupportTicketUnsubscribe"] = "unsubscribe:support-ticket";
    ClientEvent["SupportTicketSendMessage"] = "send:support-ticket-message";
    ClientEvent["Authenticate"] = "authenticate";
})(ClientEvent || (ClientEvent = {}));
export var Room;
(function (Room) {
    Room["PoolsActivity"] = "pools-activity";
})(Room || (Room = {}));
export const buildPairEventsRoomKey = (network, event) => `pair-events:${network}:${event}`;
export const buildPairEventsFilterRoomKey = (network, event, filterHash) => `pair-events:${network}:${event}:${filterHash}`;
export const buildPairEventsFilterDefsKey = (network, event) => `pair-events:filter-defs:${network}:${event}`;
export const buildPairEventsActiveHashesKey = (network, event) => `pair-events:active:${network}:${event}`;
export const PAIR_EVENTS_ACTIVE_KEY_PREFIX = 'pair-events:active:';
export const buildUserOrdersRoomKey = (delugeId, network) => {
    return `user-orders:${delugeId}:${network}`;
};
const DEFAULT_SOCKET_PROTOCOL_VERSION = 1;
const DEFAULT_SOCKET_PAYLOAD_ENCODING = 'json';
export const buildPoolInfoUpdateRoomKey = (address, network, options = {}) => {
    if (options.updateMode !== 'partial') {
        return `pool-info-update:${network}:${address}`;
    }
    return [
        'pool-info-update',
        network,
        address,
        'partial',
        options.protocolVersion ?? DEFAULT_SOCKET_PROTOCOL_VERSION,
        options.encoding ?? DEFAULT_SOCKET_PAYLOAD_ENCODING,
    ].join(':');
};
export const isPoolInfoUpdateRoomKey = (roomKey) => {
    return roomKey.startsWith('pool-info-update:');
};
export const detachPoolInfoUpdateRoomKey = (roomKey) => {
    const [prefix, network, pairAddress, updateMode, protocolVersion, encoding] = roomKey.split(':');
    return {
        prefix,
        pairAddress,
        network,
        updateMode: updateMode === 'partial'
            ? 'partial'
            : 'full',
        protocolVersion: protocolVersion === '1' ? 1 : undefined,
        encoding: encoding === 'json'
            ? 'json'
            : undefined,
    };
};
export const isHolderChartRoomKey = (roomKey) => {
    return roomKey.startsWith('holder-chart:');
};
export const buildHolderChartRoomKey = (pairAddress, network) => {
    return `holder-chart:${pairAddress}:${network}`;
};
export const detachHolderChartRoomKey = (roomKey) => {
    const [prefix, pairAddress, network] = roomKey.split(':');
    return { prefix, pairAddress, network };
};
export const buildUserOnlineStatusRoomKey = (delugeId) => {
    return `user-online-status:${delugeId}`;
};
export const isUserOnlineStatusRoomKey = (roomKey) => {
    return roomKey.startsWith('user-online-status:');
};
export const detachUserOnlineStatusRoomKey = (roomKey) => {
    const [prefix, delugeId] = roomKey.split(':');
    return { prefix, delugeId };
};
export const buildSupportTicketRoomKey = (ticketId) => {
    return `support-ticket:${ticketId}`;
};
export const buildSupportUserTicketsRoomKey = (delugeId) => {
    return `support-user-tickets:${delugeId}`;
};
//# sourceMappingURL=index.js.map